import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { tenantFetch } from "../lib/api-client";

interface ShopifyConnection {
  id: string;
  shop_domain: string;
  shop_name: string;
  status: string;
  products_synced: number;
  last_sync_at: string;
  created_at: string;
}

interface BillingUsage {
  subscription: {
    tier: string;
    status: string;
    priceMonthly: number;
  };
  usage: {
    tradeIns: { current: number; limit: number; percentage: number };
    products: { current: number; limit: number; percentage: number };
  };
  warnings: { type: string; message: string }[];
}

export const Route = createFileRoute("/settings/shopify" as any)({
  component: ShopifySettings,
});

function ShopifySettings() {
  const [connection, setConnection] = useState<ShopifyConnection | null>(null);
  const [billing, setBilling] = useState<BillingUsage | null>(null);
  const [shopDomain, setShopDomain] = useState("");
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchConnection();
    fetchBilling();
  }, []);

  const fetchConnection = async () => {
    try {
      const response = await tenantFetch("/api/v1/connectors/shopify/status");
      if (response.ok) {
        const data = await response.json();
        // Status returns { connections: [...] } — use the first active one
        const active = data.connections?.find(
          (c: ShopifyConnection) => c.status === "active",
        );
        if (active) {
          setConnection(active);
        }
      }
    } catch (err) {
      console.error("Failed to fetch connection:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBilling = async () => {
    try {
      const response = await tenantFetch("/api/v1/billing/usage");
      if (response.ok) {
        const data = await response.json();
        setBilling(data);
      }
    } catch (err) {
      console.error("Failed to fetch billing:", err);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);

    try {
      // Try to connect using pre-configured environment credentials first
      const response = await tenantFetch(
        `/api/v1/connectors/shopify/connect-configured`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      const data = await response.json();

      if (data.success && data.connection) {
        setConnection(data.connection);
        setError(null);
      } else if (data.error?.includes("not configured")) {
        // No pre-configured credentials, need OAuth flow
        if (!shopDomain.trim()) {
          setError("Please enter your Shopify store domain");
          return;
        }
        // Try OAuth flow
        const oauthResponse = await tenantFetch(
          `/api/v1/connectors/shopify/install`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ shop: shopDomain }),
          },
        );
        const oauthData = await oauthResponse.json();
        if (oauthData.authorizationUrl) {
          window.location.href = oauthData.authorizationUrl;
        } else {
          setError(oauthData.error || "Failed to initiate connection");
        }
      } else {
        setError(data.error || "Failed to connect");
      }
    } catch (err) {
      setError("Connection failed. Please try again.");
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const response = await tenantFetch(
        `/api/v1/connectors/shopify/sync-configured`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        },
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Update connection with new sync info
        if (connection) {
          setConnection({
            ...connection,
            products_synced: data.processed,
            last_sync_at: new Date().toISOString(),
          });
        }
        alert(`Synced ${data.processed} products from Shopify!`);
      } else {
        setError(data.error || "Sync failed");
      }
    } catch (err) {
      console.error("Sync failed:", err);
      setError("Sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm("Are you sure you want to disconnect your Shopify store?"))
      return;

    try {
      const response = await tenantFetch(
        `/api/v1/connectors/shopify/disconnect?connectionId=${connection?.id}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );

      if (response.ok) {
        setConnection(null);
      }
    } catch (err) {
      console.error("Disconnect failed:", err);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-2 text-sm text-penta-grey-medium">
          <Link to="/settings" className="hover:text-penta-black">
            Settings
          </Link>
          <span>/</span>
          <span className="text-penta-black">Shopify</span>
        </div>
        <div className="animate-pulse">
          <div className="h-8 bg-penta-grey-light rounded w-48 mb-6"></div>
          <div className="h-32 bg-penta-grey-light/50 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-penta-grey-medium">
        <Link to="/settings" className="hover:text-penta-black">
          Settings
        </Link>
        <span>/</span>
        <span className="text-penta-black">Shopify Integration</span>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#96bf48] flex items-center justify-center">
          <svg
            className="w-6 h-6 text-white"
            viewBox="0 0 109.5 124.5"
            fill="currentColor"
          >
            <path d="M95.9 23.9c-.1-.7-.7-1-1.2-1.1-.5 0-10.8-.8-10.8-.8s-7.2-7-7.9-7.8c-.7-.7-2.2-.5-2.8-.3 0 0-1.4.4-3.8 1.2-2.3-6.6-6.3-12.7-13.4-12.7-.2 0-.4 0-.6 0-2-2.6-4.5-3.8-6.6-3.8C29.8-1.5 20.8 23.4 18 35.2c-7.2 2.2-12.3 3.8-13 4-4 1.3-4.1 1.4-4.6 5.2C0 47.7 0 124.5 0 124.5l75.6 13.1 33.9-8.3S96 24.6 95.9 23.9zM69.4 16.2c-1.8.6-3.9 1.2-6.2 1.9V16c0-4.7-.6-8.5-1.7-11.5 4.2.6 7 5.3 7.9 11.7zm-11.7 3.6c-4.2 1.3-8.8 2.7-13.4 4.2 1.3-5 3.8-10 8.4-13.2 1.8-1.3 4.3-2.7 7.2-3.5.8 2.5 1.8 6.1 1.8 10.4v2.1h-4zm-8.5-16.3c1.5 0 3 .3 4.5 1.1-2.7 1.3-5.3 3.3-7.7 5.9-6.4 7-11.3 17.9-13.4 28.4-3.8 1.2-7.4 2.3-10.8 3.3 3-14.9 14.8-37.6 27.4-38.7z" />
          </svg>
        </div>
        <div>
          <h1 className="text-h3 text-penta-black">Shopify Integration</h1>
          <p className="text-penta-grey-medium">
            Connect your store to enable the Tonic trade-in widget
          </p>
        </div>
      </div>

      {/* Connection Status */}
      {connection ? (
        <div className="space-y-6">
          {/* Connected Store Card */}
          <div className="card">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-penta-mint-green/20 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-penta-mint-green"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-penta-black">
                    {connection.shop_name || connection.shop_domain}
                  </h3>
                  <p className="text-penta-grey-medium text-sm">
                    {connection.shop_domain}
                  </p>
                </div>
              </div>
              <span className="px-3 py-1 bg-penta-mint-green/20 text-penta-black text-sm font-medium rounded-full">
                Connected
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-penta-grey-light">
              <div>
                <p className="text-penta-grey-medium text-sm">
                  Products Synced
                </p>
                <p className="text-2xl font-semibold text-penta-black">
                  {connection.products_synced}
                </p>
              </div>
              <div>
                <p className="text-penta-grey-medium text-sm">Last Sync</p>
                <p className="text-sm text-penta-black">
                  {connection.last_sync_at
                    ? new Date(connection.last_sync_at).toLocaleString()
                    : "Never"}
                </p>
              </div>
              <div>
                <p className="text-penta-grey-medium text-sm">Connected</p>
                <p className="text-sm text-penta-black">
                  {new Date(connection.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-6 border-t border-penta-grey-light">
              <button
                onClick={handleSync}
                disabled={syncing}
                className="btn-primary"
              >
                {syncing ? "Syncing..." : "Sync Products"}
              </button>
              <button
                onClick={handleDisconnect}
                className="px-4 py-2 text-penta-coral border border-penta-coral/30 rounded-full hover:bg-penta-coral/10"
              >
                Disconnect
              </button>
            </div>
          </div>

          {/* Usage & Billing */}
          {billing && (
            <div className="card">
              <h3 className="font-semibold text-penta-black mb-4">
                Usage & Billing
              </h3>

              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="text-sm text-penta-grey-medium">
                    Current Plan
                  </span>
                  <p className="text-lg font-semibold capitalize text-penta-black">
                    {billing.subscription.tier}
                  </p>
                </div>
                <button
                  onClick={() => (window.location.href = "/settings")}
                  className="btn-secondary"
                >
                  Manage Plan
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-penta-grey-medium">
                      Trade-ins this month
                    </span>
                    <span className="text-penta-black font-medium">
                      {billing.usage.tradeIns.current} /{" "}
                      {billing.usage.tradeIns.limit === -1
                        ? "∞"
                        : billing.usage.tradeIns.limit}
                    </span>
                  </div>
                  <div className="h-2 bg-penta-grey-light rounded-full overflow-hidden">
                    <div
                      className="h-full bg-penta-mint-green rounded-full transition-all"
                      style={{
                        width: `${Math.min(billing.usage.tradeIns.percentage, 100)}%`,
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-penta-grey-medium">
                      Products synced
                    </span>
                    <span className="text-penta-black font-medium">
                      {billing.usage.products.current} /{" "}
                      {billing.usage.products.limit === -1
                        ? "∞"
                        : billing.usage.products.limit}
                    </span>
                  </div>
                  <div className="h-2 bg-penta-grey-light rounded-full overflow-hidden">
                    <div
                      className="h-full bg-penta-mint-green rounded-full transition-all"
                      style={{
                        width: `${Math.min(billing.usage.products.percentage, 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>

              {billing.warnings.length > 0 && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200">
                  {billing.warnings.map((warning, i) => (
                    <p key={i} className="text-sm text-amber-800">
                      {warning.message}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Widget Code */}
          <div className="card">
            <h3 className="font-semibold text-penta-black mb-4">
              Trade-In Widget
            </h3>
            <p className="text-penta-grey-medium text-sm mb-4">
              The trade-in widget is automatically added to your product pages
              when installed via Shopify. Copy the code below to manually embed
              it anywhere.
            </p>
            <div className="bg-penta-grey-light p-4 font-mono text-sm overflow-x-auto">
              <code className="text-penta-black">
                {`<script src="https://agentic-commerce.pentatonic.com/widget/tonic.js" 
  data-shop="${connection.shop_domain}"
  async></script>`}
              </code>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  `<script src="https://agentic-commerce.pentatonic.com/widget/tonic.js" data-shop="${connection.shop_domain}" async></script>`,
                );
              }}
              className="mt-3 px-3 py-1.5 text-sm border border-penta-grey-medium/30 rounded-full hover:bg-penta-grey-light"
            >
              Copy Code
            </button>
          </div>
        </div>
      ) : (
        /* Connect Form */
        <div className="card max-w-lg">
          <h2 className="text-lg font-semibold text-penta-black mb-2">
            Connect Your Store
          </h2>
          <p className="text-penta-grey-medium mb-6">
            Enter your Shopify store domain to get started with the Tonic
            trade-in widget.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-penta-coral/10 border border-penta-coral/30 text-penta-coral text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-penta-black mb-1">
                Store Domain
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={shopDomain}
                  onChange={(e) => setShopDomain(e.target.value)}
                  placeholder="your-store"
                  className="flex-1 px-4 py-2 border border-penta-grey-medium/30 rounded-l-lg focus:ring-2 focus:ring-penta-mint-green focus:border-transparent"
                />
                <span className="px-4 py-2 bg-penta-grey-light border border-l-0 border-penta-grey-medium/30 rounded-r-lg text-penta-grey-medium">
                  .myshopify.com
                </span>
              </div>
            </div>

            <button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full px-4 py-3 bg-[#96bf48] text-white font-medium rounded-full hover:bg-[#7ea83c] disabled:opacity-50 transition-colors"
            >
              {connecting ? "Connecting..." : "Connect Shopify"}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-penta-grey-light">
            <h4 className="text-sm font-medium text-penta-black mb-2">
              What we'll access:
            </h4>
            <ul className="space-y-2 text-sm text-penta-grey-medium">
              <li className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-penta-mint-green"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Product catalog (read-only)
              </li>
              <li className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-penta-mint-green"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Inventory levels (read-only)
              </li>
              <li className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-penta-mint-green"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Orders (to track trade-in redemptions)
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
